import type { ThirdPartyFeatureSettings } from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { Switch } from "@/components/ui/switch";
import type { Vendor } from "@/components/vendor-info";
import * as thirdPartyApi from "@/lib/api/third-party-features";
import { useIduraSettings } from "@/lib/hooks/use-idura";
import { useSumsubSettings } from "@/lib/hooks/use-sumsub";
import { useTrapetsSettings } from "@/lib/hooks/use-trapets";
import { useT } from "@/lib/i18n/use-t";
import { TrapetsSettings } from "../trapets/trapets-settings";
import { FeatureCard, type PriceLine } from "./feature-card";
import { IduraSettings } from "./idura-settings";
import { T } from "./page.i18n";
import { SumsubSettings } from "./sumsub-settings";

const WHEREBY_PRICES: PriceLine[] = [
  { label: "Cloud Recording", unit: "Per minute", price: "0.015 EUR" },
  { label: "Session Transcripts", unit: "Per minute / unmuted participant", price: "0.010 EUR" },
  { label: "Participant Minutes", unit: "Per minute / participant", price: "0.006 EUR" },
];

const FILE_TRANSCRIPTION_PRICES: PriceLine[] = [
  { label: "Document OCR (Mistral)", unit: "Per page", price: "0.008 EUR" },
  {
    label: "Audio transcription (Scaleway hosted Whisper Large)",
    unit: "Per minute",
    price: "0.005 EUR",
  },
];

const WEB_TOOLS_PRICES: PriceLine[] = [
  { label: "Web fetch / scrape", unit: "Per fetched page", price: "0.002 EUR" },
  { label: "Web search", unit: "Per search", price: "0.003 EUR" },
];

const ENIN_PRICES: PriceLine[] = [
  { label: "ENIN Lookup", unit: "Per company lookup", price: "0.400 EUR" },
];

// Sub-processor identities. Legal details + links are sourced from each vendor's own
// official pages; slots with no public page are omitted rather than guessed.
const WHEREBY_VENDOR: Vendor = {
  name: "Whereby AS",
  homepage: "https://whereby.com",
  details: ["Gate 1, no. 107, 6700 Måløy, Norway"],
  // Whereby publishes no public status page (status.whereby.com serves the marketing site).
  links: [
    { kind: "terms", href: "https://whereby.com/information/tos" },
    { kind: "security", href: "https://whereby.com/information/security/" },
  ],
};

const MISTRAL_VENDOR: Vendor = {
  name: "Mistral AI (SAS)",
  homepage: "https://mistral.ai",
  details: ["RCS Paris 952 418 325", "15 rue des Halles, 75001 Paris, France"],
  links: [
    { kind: "terms", href: "https://legal.mistral.ai/terms" },
    { kind: "status", href: "https://status.mistral.ai/" },
    { kind: "security", href: "https://trust.mistral.ai/" },
  ],
};

// Audio transcription runs on Scaleway's EU-hosted Whisper (whisper-large-v3),
// not Mistral — Voxtral can't handle the Nordic languages most clients use.
const SCALEWAY_VENDOR: Vendor = {
  name: "Scaleway SAS",
  homepage: "https://www.scaleway.com",
  details: ["RCS Paris 433 115 904", "8 rue de la Ville l'Évêque, 75008 Paris, France"],
  links: [
    { kind: "terms", href: "https://www.scaleway.com/en/contracts/" },
    { kind: "status", href: "https://status.scaleway.com/" },
    { kind: "security", href: "https://security.scaleway.com/" },
  ],
};

const FIRECRAWL_VENDOR: Vendor = {
  name: "SideGuide Technologies, Inc. (d/b/a Firecrawl)",
  homepage: "https://www.firecrawl.dev",
  details: ["Delaware, United States"],
  links: [
    { kind: "terms", href: "https://www.firecrawl.dev/terms-of-service" },
    { kind: "status", href: "https://status.firecrawl.dev/" },
    { kind: "security", href: "https://trust.firecrawl.dev/" },
  ],
};

const ENIN_VENDOR: Vendor = {
  name: "Enin AS",
  homepage: "https://www.enin.ai",
  details: ["Org. No. 917 540 640", "Grundingen 2, 0250 Oslo, Norway"],
  // No public terms / status / security pages; the API documentation is the only public resource.
  links: [{ kind: "documentation", href: "https://docs.enin.ai/en/api" }],
};

const SINCH_PRICES: PriceLine[] = [{ label: "SMS", unit: "Per message", price: "0.099 EUR" }];

// Idura is a mode-based feature (disabled/test/production) — its price + vendor
// disclosure live in <IduraSettings>, mirroring Trapets/Sumsub.

const SINCH_VENDOR: Vendor = {
  name: "Sinch",
  homepage: "https://sinch.com",
  links: [
    {
      kind: "terms",
      href: "https://sinch.com/legal/terms-and-conditions/other-sinch-terms-conditions/general-terms-and-conditions/",
    },
    { kind: "status", href: "https://status.sinch.com/" },
    { kind: "security", href: "https://trust.sinch.com/" },
  ],
};

export default function ThirdPartySettingsPage() {
  const t = useT(T);
  const queryClient = useQueryClient();
  const trapets = useTrapetsSettings();
  const sumsub = useSumsubSettings();
  const idura = useIduraSettings();
  const { data: features, isLoading: featuresLoading } = useQuery({
    queryKey: ["third-party-features"],
    queryFn: () => thirdPartyApi.getThirdPartyFeatures(),
  });

  // Auto-save a single boolean toggle: optimistic update, reconcile with the server
  // response, and refetch on failure so the switch reflects the persisted state.
  const toggle = async (key: keyof ThirdPartyFeatureSettings, value: boolean) => {
    queryClient.setQueryData<ThirdPartyFeatureSettings>(["third-party-features"], (old) =>
      old ? { ...old, [key]: value } : old,
    );
    try {
      const updated = await thirdPartyApi.updateThirdPartyFeatures({ [key]: value });
      queryClient.setQueryData(["third-party-features"], updated);
    } catch {
      queryClient.invalidateQueries({ queryKey: ["third-party-features"] });
    }
  };

  if (trapets.isLoading || sumsub.isLoading || featuresLoading || !features) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.title }]}
        title={t.title}
        description={t.description}
      />

      <SumsubSettings sumsubMode={sumsub.settings?.sumsubMode || "test"} mutate={sumsub.mutate} />

      <TrapetsSettings
        trapetsMode={trapets.settings?.trapetsMode || "test"}
        mutate={trapets.mutate}
      />

      <FeatureCard
        title={t.whereby}
        description={t.wherebyDesc}
        prices={WHEREBY_PRICES}
        vendor={WHEREBY_VENDOR}
        control={
          <Switch
            aria-label={t.whereby}
            checked={features.wherebyEnabled}
            onCheckedChange={(v) => toggle("wherebyEnabled", v)}
          />
        }
      />

      <FeatureCard
        title={t.fileTranscription}
        description={t.fileTranscriptionDesc}
        prices={FILE_TRANSCRIPTION_PRICES}
        vendor={[MISTRAL_VENDOR, SCALEWAY_VENDOR]}
        control={
          <Switch
            aria-label={t.fileTranscription}
            checked={features.fileTranscriptionEnabled}
            onCheckedChange={(v) => toggle("fileTranscriptionEnabled", v)}
          />
        }
      />

      <FeatureCard
        title={t.webTools}
        description={t.webToolsDesc}
        prices={WEB_TOOLS_PRICES}
        vendor={FIRECRAWL_VENDOR}
        control={
          <Switch
            aria-label={t.webTools}
            checked={features.webToolsEnabled}
            onCheckedChange={(v) => toggle("webToolsEnabled", v)}
          />
        }
      />

      <FeatureCard
        title={t.enin}
        description={t.eninDesc}
        prices={ENIN_PRICES}
        vendor={ENIN_VENDOR}
        control={
          <Switch
            aria-label={t.enin}
            checked={features.eninEnabled}
            onCheckedChange={(v) => toggle("eninEnabled", v)}
          />
        }
      />

      <IduraSettings iduraMode={idura.settings?.iduraMode || "test"} mutate={idura.mutate} />

      <FeatureCard
        title={t.sinch}
        description={t.sinchDesc}
        prices={SINCH_PRICES}
        vendor={SINCH_VENDOR}
        control={
          <Switch
            aria-label={t.sinch}
            checked={features.sinchEnabled}
            onCheckedChange={(v) => toggle("sinchEnabled", v)}
          />
        }
      />
    </div>
  );
}
